import type { Message } from '../../flora.type';

interface TurnFeedbackEligibilityArgs {
  index: number;
  questionsLength: number;
  isActiveQuestion: boolean;
  isWaitingForFirstResponse: boolean;
  latestTurnIsLive?: boolean;
  chatHistoryId?: string;
  message: Message | undefined;
}

/**
 * Whether a given assistant turn is eligible for the thumbs up/down feedback control.
 *
 * Feedback is offered only on the most-recent turn, only once streaming has fully
 * stopped, only when that turn was produced live this session (`latestTurnIsLive`),
 * only when a chat id is available, and never while a HITL question or tool approval
 * is still pending on the turn. Used by `AgentMessageList`, which renders both the side
 * panel and the full-page Flora chat.
 *
 * @param {TurnFeedbackEligibilityArgs} args - The turn's position and the current chat state.
 * @returns {boolean} True when the feedback control should render for this turn.
 */
const isTurnFeedbackEligible = ({
  index,
  questionsLength,
  isActiveQuestion,
  isWaitingForFirstResponse,
  latestTurnIsLive,
  chatHistoryId,
  message,
}: TurnFeedbackEligibilityArgs): boolean =>
  index === questionsLength - 1 &&
  !isActiveQuestion &&
  !isWaitingForFirstResponse &&
  Boolean(latestTurnIsLive) &&
  Boolean(chatHistoryId) &&
  !message?.pendingQuestions?.length &&
  !message?.pendingToolApproval;

/**
 * Whether the chat's most-recent turn was produced live in the current session
 * and streaming has fully stopped — the gate that separates a stream the user
 * just watched from a turn loaded out of chat history or restored after reload.
 *
 * True only when nothing is streaming, the current thread is the one streamed live
 * this session (`streamedThreadId === threadId`, including cache-restored streams),
 * and there is at least one message. Pure and fully derived from its args — callers
 * pass `streamedThreadRef.current`, the ref they also use to guard the chat-history
 * load from overwriting a live stream.
 *
 * @param {boolean} isActiveQuestion - Whether a question is currently being processed.
 * @param {boolean} isWaitingForFirstResponse - Whether the first response chunk is still pending.
 * @param {string} threadId - The current chat/thread id.
 * @param {string | null} streamedThreadId - Id of the thread streamed live this session (the caller's `streamedThreadRef.current`).
 * @param {number} messagesCount - Number of assistant turns currently rendered.
 * @returns {boolean} Whether the latest turn is live this session and streaming has stopped.
 */
const isLatestTurnLive = (
  isActiveQuestion: boolean,
  isWaitingForFirstResponse: boolean,
  threadId: string,
  streamedThreadId: string | null,
  messagesCount: number,
): boolean =>
  !isActiveQuestion &&
  !isWaitingForFirstResponse &&
  streamedThreadId === threadId &&
  Boolean(threadId) &&
  messagesCount > 0;

export { isLatestTurnLive, isTurnFeedbackEligible };
